import { useEffect } from 'react';

import { useFeatureHomePage } from 'components/features/home-page/use-feature-home-page';
import { useFeatureNavigation } from 'hooks/use-feature-navigation';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { setUserUtmAttributes } from 'state/cdp/mParticle/set-user-attributes';
import { useLocationContext } from 'state/location';
import { useStoreContext } from 'state/store';
import * as location from 'utils/location';
import { routes } from 'utils/routing';

export const useLogPageView = () => {
  const { featureNavigationMobile } = useFeatureNavigation();
  const {
    eventPageName,
    location: { search, pathname },
    pathMatches,
    setEventPageName,
  } = useLocationContext();
  const { logPageView, trackEvent } = useCdpContext();
  const { homePageId } = useFeatureHomePage();
  const { restaurantLogAttributesRef } = useStoreContext();

  // List of paths to be excluded from logging
  const excludedPaths = new Set([
    '/',
    '/123-de-app',
    '/about',
    '/account/info',
    '/account/orders',
    '/account/payment/add-card',
    '/app-mybk',
    '/azienda',
    '/bk-days-de',
    '/bk-days-de-app',
    '/bk-days-en',
    '/bk-days-faq',
    '/bk-days-fr',
    '/bk-days-it',
    '/bk-verse',
    '/bonuspolitik',
    '/brametest',
    '/careers',
    '/carriera',
    '/carriere',
    '/cart',
    '/cheese-lovers',
    '/cheesyroestilover',
    '/chicken-kings',
    '/clean-menu',
    '/company',
    '/conditions-recompenses',
    '/condizioni%20di%20consegna',
    '/connectez-vous',
    '/consegna',
    '/contact',
    '/contact-test',
    '/contact-us',
    '/contatto',
    '/crazy-cheese-bbq',
    '/crown-standards',
    '/datenschutz',
    '/de-app',
    '/de-bk-days-faq',
    '/de-cheese-lovers',
    '/de-chicken-kings',
    '/de-clean-menu',
    '/de-cookie-policy',
    '/de-crazy-cheese-bbq',
    '/de-desserts',
    '/de-duo-bacon-cheddar',
    '/de-faq',
    '/de-king-deals',
    '/de-myburgerking',
    '/de-mysteryburger',
    '/de-nutrition-explorer',
    '/de-origin',
    '/de-presse',
    '/de-quality-check',
    '/de-real-food',
    '/de-rewards-maintenance',
    '/de-smood',
    '/de-smood-terms-of-service',
    '/de-spin-win-policy',
    '/de-swiss-selection',
    '/de-terms-of-service',
    '/de-texas-bacon-lovers',
    '/de-uber-eats',
    '/de-veggie-kings',
    '/de/menu/sitemap-s.html',
    '/de/restaurants/index-s.html',
    '/delivery',
    '/desserts',
    '/download-app',
    '/en-cookie-policy',
    '/en/menu/sitemap-s.html',
    '/en/restaurants/index-s.html',
    '/fakten-check',
    '/famiglia-e-amici',
    '/familieundfreunde',
    '/famille-&-amis',
    '/family-&-friends',
    '/faq',
    '/fr-app',
    '/fr-bk-days-faq',
    '/fr-cheese-lovers',
    '/fr-chicken-kings',
    '/fr-crazy-cheese-bbq',
    '/fr-desserts',
    '/fr-duo-bacon-cheddar',
    '/fr-faq',
    '/fr-mozzarella-lovers',
    '/fr-myburgerking',
    '/fr-mysteryburger',
    '/fr-nutrition-explorer',
    '/fr-origin',
    '/fr-presse',
    '/fr-quality-check',
    '/fr-real-food',
    '/fr-rewards-maintenance',
    '/fr-smood',
    '/fr-smood-terms-of-service',
    '/fr-spin-win-policy',
    '/fr-swiss-selection',
    '/fr-terms-of-service',
    '/fr-texas-bacon-lovers',
    '/fr-veggie-kings',
    '/fr/restaurants/index-s.html',
    '/iframe',
    '/iframe-de',
    '/informativa-sulla-privacy',
    '/inscrivez-vous',
    '/it-app',
    '/it-cheese-lovers',
    '/it-chicken-kings',
    '/it-crazy-cheese-bbq',
    '/it-desserts',
    '/it-duo-bacon-cheddar',
    '/it-faq',
    '/it-just-eat',
    '/it-king-deals',
    '/it-myburgerking',
    '/it-mysteryburger',
    '/it-nutrition-explorer',
    '/it-quality-check',
    '/it-real-food',
    '/it-rewards-maintenance',
    '/it-smood',
    '/it-smood-terms-of-service',
    '/it-spin-win-policy',
    '/it-swiss-selection',
    '/it-terms-of-service',
    '/it-texas-bacon-lovers',
    '/it-uber-eats',
    '/it-veggie-kings',
    '/karriere',
    '/king-deals',
    '/kontakt',
    '/lieferservice',
    '/livraison',
    '/localisateur-de-magasin',
    '/menu',
    '/menu/combo',
    '/menu/item',
    '/menu/picker',
    '/menu/section',
    '/myburgerking',
    '/mysteryburger',
    '/nutrition-explorer',
    '/offers',
    '/offerteepromozioni',
    '/order-confirmation',
    '/origin',
    '/participating-stores',
    '/politica-ricompense',
    '/politique-confidentialite',
    '/press',
    '/privacy-policy',
    '/promosetactions',
    '/promosundaktionen',
    '/quality-check-de',
    '/real-food',
    '/rewards',
    '/rewards-maintenance',
    '/rewards-policy',
    '/rewards/details',
    '/signin',
    '/signup',
    '/smood',
    '/smood-terms-of-service',
    '/societe',
    '/spin-win-policy',
    '/store-locator',
    '/store-locator/store',
    '/swiss-selection',
    '/terms',
    '/terms-of-service',
    '/texas-bacon-lovers',
    '/uber-eats',
    '/unternehmen',
    '/veggie-kings',
    '/veggie-kings-de',
    '/veggie-kings-fr',
    '/veggie-kings-it',
  ]);

  useEffect(() => {
    if (featureNavigationMobile.length > 0) {
      // Skip logging if pathname is in the excluded paths
      if (excludedPaths.has(pathname)) {
        return;
      }

      let linkName: string = '';
      if (!eventPageName || eventPageName === '') {
        const [nav] = featureNavigationMobile.filter((navMobile: any) =>
          pathMatches(navMobile?.tabLink?.link?.locale || '')
        );
        linkName = nav?.tabLink?.text?.locale || '';
        setEventPageName(linkName);
      }
      // get mParticle user object with current attributes
      const user = window.mParticle?.Identity?.getCurrentUser?.(); // TODO: Check with Bloomreach after
      // add the UTM attributes conteined on URL to mParticle user object
      setUserUtmAttributes(user, new URLSearchParams(search));

      // determine attributes to send to the events below
      const referrer = document.referrer || location.get('href');
      const attrs = {
        pathname,
        pathWithQuery: pathname + search,
        sanityId: pathname === routes.base ? homePageId.featureHomePageId : '',
        referrer,
        Name: eventPageName || linkName,
        ...restaurantLogAttributesRef.current,
      };
      // Flags for google analytics integration
      const flags = {
        'Google.Page': pathname,
        'Google.DocumentReferrer': referrer,
      };
      // mParticle page view event - uses mParticle logPageView API to track page view
      logPageView(pathname, attrs, flags);
      // mParticle custom event for page view - uses mParticle logEvent API to track page view
      trackEvent({
        name: CustomEventNames.PAGE_VIEW,
        type: EventTypes.Other,
        attributes: {
          ...attrs,
          ...flags,
          path: pathname,
        },
      });

      setEventPageName(undefined);
    }
  }, [pathname, featureNavigationMobile]); // eslint-disable-line react-hooks/exhaustive-deps
};
